import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  BarChart3,
  Bell,
  Sparkles,
  List,
  LineChart,
  LogOut,
  CreditCard
} from 'lucide-react';
import api from '../services/api';
import TicketSubmissionWidget from './TicketSubmissionWidget';
import TicketsPage from './TicketsPage';
import AnalyticsPage from './AnalyticsPage';
import AIInsightsPage from './AIInsightsPage';
import BillingPage from './BillingPage';
import { useAuth } from '../contexts/AuthContext';
import {
  getPendingPlan,
  clearPendingPlan,
  startCheckout,
  handleCheckoutError,
  readCheckoutQueryParams,
  clearCheckoutQueryParams,
  pollCheckoutStatus,
} from '../services/checkout';
import { toast } from 'sonner';

const Dashboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [aiInsights, setAIInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, company, logout, refresh } = useAuth();
  const handledReturnRef = useRef(false);
  const handledPendingRef = useRef(false);

  // Company ID comes from auth context now — no hardcoding.
  const COMPANY_ID = company?.id;

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboardData();
    }
  }, [activeTab]);

  // Handle return from Stripe checkout
  useEffect(() => {
    if (handledReturnRef.current) return;
    const { checkout, sessionId } = readCheckoutQueryParams();
    if (!checkout) return;
    handledReturnRef.current = true;

    if (checkout === 'success' && sessionId) {
      setActiveTab('billing');
      toast.loading('Confirming your payment...', { id: 'checkout-poll' });
      pollCheckoutStatus(sessionId)
        .then((status) => {
          toast.dismiss('checkout-poll');
          if (status && status.payment_status === 'paid') {
            toast.success('Payment confirmed! You are upgraded.');
            refresh();
          } else {
            toast.message('Payment is still processing. Refresh in a moment.');
          }
        })
        .catch(() => {
          toast.dismiss('checkout-poll');
          toast.error('Could not verify payment status. Please refresh.');
        })
        .finally(() => clearCheckoutQueryParams());
    } else if (checkout === 'canceled') {
      toast.message('Checkout canceled. No charges were made.');
      clearCheckoutQueryParams();
    }
  }, [refresh]);

  // If a plan was selected on the landing page before signup, auto-start checkout
  useEffect(() => {
    if (handledPendingRef.current) return;
    if (!company) return;
    const pending = getPendingPlan();
    if (!pending) return;
    handledPendingRef.current = true;
    clearPendingPlan();
    // Defer to next tick so component finishes mount before redirect
    setTimeout(() => {
      startCheckout(pending.priceId, pending.seats).catch((err) => {
        handleCheckoutError(err);
      });
    }, 200);
  }, [company]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, insightsData] = await Promise.all([
        api.getDashboardStats(),
        api.getAIInsights()
      ]);
      setStats(statsData || {});
      setAIInsights(insightsData || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setStats({});
      setAIInsights([]);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-900/30 text-green-400 border-green-800';
      case 'in_progress': return 'bg-blue-900/30 text-blue-400 border-blue-800';
      case 'open': return 'bg-yellow-900/30 text-yellow-400 border-yellow-800';
      default: return 'bg-gray-800 text-gray-400';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-900/30 text-red-400 border-red-800';
      case 'medium': return 'bg-orange-900/30 text-orange-400 border-orange-800';
      case 'low': return 'bg-gray-800 text-gray-400 border-gray-700';
      default: return 'bg-gray-800 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => onNavigate('landing')}>
                <div className="w-10 h-10 flex items-center justify-center">
                  <svg viewBox="0 0 48 48" className="w-10 h-10">
                    <rect x="8"  y="11" width="32" height="6" rx="2" fill="#111827" />
                    <rect x="8"  y="21" width="22" height="6" rx="2" fill="#374151" />
                    <rect x="8"  y="31" width="12" height="6" rx="2" fill="#FCD34D" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-lg font-bold">Simplified Helpdesk</h1>
                  <p className="text-xs text-gray-400">Automation-first IT support</p>
                </div>
              </div>
              <nav className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={activeTab === 'dashboard' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900'}
                  onClick={() => setActiveTab('dashboard')}
                >
                  <BarChart3 className="w-4 h-4 mr-1" />
                  Dashboard
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={activeTab === 'tickets' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900'}
                  onClick={() => setActiveTab('tickets')}
                >
                  <List className="w-4 h-4 mr-1" />
                  Tickets
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className={activeTab === 'analytics' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900'}
                  onClick={() => setActiveTab('analytics')}
                >
                  <LineChart className="w-4 h-4 mr-1" />
                  Analytics
                </Button>
                <Button 
                  data-testid="nav-tab-insights"
                  variant="ghost" 
                  size="sm" 
                  className={activeTab === 'insights' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900'}
                  onClick={() => setActiveTab('insights')}
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  AI Insights
                </Button>
                <Button 
                  data-testid="nav-tab-billing"
                  variant="ghost" 
                  size="sm" 
                  className={activeTab === 'billing' ? 'text-gray-900 bg-gray-100' : 'text-gray-600 hover:text-gray-900'}
                  onClick={() => setActiveTab('billing')}
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Billing
                </Button>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex flex-col items-end mr-2">
                <span className="text-sm font-medium text-gray-900" data-testid="dashboard-user-name">{user?.name}</span>
                <span className="text-xs text-gray-500">{company?.name}</span>
              </div>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-gray-900 relative">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full"></span>
              </Button>
              <Button
                data-testid="dashboard-logout-btn"
                variant="ghost"
                size="icon"
                className="text-gray-600 hover:text-gray-900"
                onClick={async () => { await logout(); onNavigate('landing'); }}
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </Button>
              <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center text-sm font-semibold text-white" data-testid="dashboard-user-avatar">
                {(user?.name || user?.email || '?').slice(0, 2).toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Render different pages based on active tab */}
        {activeTab === 'dashboard' && (
          loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-lg text-gray-600">Loading...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Dashboard Overview */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Dashboard Overview</h2>
                <p className="text-gray-600 mt-1">Welcome back! Here's what's happening</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-6">
                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <BarChart3 className="w-5 h-5 text-blue-500" />
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">+12%</Badge>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stats.total_tickets}</div>
                    <div className="text-sm text-gray-600">Total Tickets</div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Sparkles className="w-5 h-5 text-green-500" />
                      <Badge className="bg-green-100 text-green-800 border-green-200">Live</Badge>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stats.ai_handled_percent}%</div>
                    <div className="text-sm text-gray-600">AI Handled</div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">⏱️</span>
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">-40%</Badge>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stats.avg_resolution_time}</div>
                    <div className="text-sm text-gray-600">Avg Resolution</div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">✅</span>
                      <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200">{stats.resolved_today}</Badge>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{stats.open_tickets}</div>
                    <div className="text-sm text-gray-600">Open Tickets</div>
                  </CardContent>
                </Card>
              </div>

              {/* AI Insights */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">AI Insights</h3>
                <div className="grid grid-cols-3 gap-4">
                  {aiInsights.map((insight, idx) => (
                    <Card key={idx} className="border-gray-200 hover:border-cyan-300 transition-colors">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <Badge className={`${
                            insight.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
                            insight.type === 'alert' ? 'bg-red-100 text-red-800 border-red-200' :
                            'bg-blue-100 text-blue-800 border-blue-200'
                          }`}>
                            {insight.type}
                          </Badge>
                          <Sparkles className="w-4 h-4 text-gray-400" />
                        </div>
                        <CardTitle className="text-sm mt-2">{insight.title}</CardTitle>
                        <CardDescription className="text-xs">
                          {insight.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )
        )}

        {activeTab === 'tickets' && <TicketsPage />}
        {activeTab === 'analytics' && <AnalyticsPage />}
        {activeTab === 'insights' && <AIInsightsPage />}
        {activeTab === 'billing' && <BillingPage />}
      </div>

      {/* Floating Help Widget */}
      <TicketSubmissionWidget
        companyId={company?.id}
        requesterName={user?.name}
        requesterEmail={user?.email}
      />
    </div>
  );
};

export default Dashboard;
